import React, { FC, useContext } from 'react';
import { useFormikContext } from 'formik';
import { Row, Column, FormInput } from 'Shared/components';
import { DriverAttributeOptionsType } from '.';
import { CoveredPartyPayloadType } from '@clearcover/core-app-types';
import styles from './styles.module.css';
import { env } from 'Shared/constants/env';
import { DriverStudentInfo, DriverStudentInfoProps } from './DriverStudentInfo';
import { PolicyContext } from 'Policies/utils';

type DriverAttributesProps = {
  options: DriverAttributeOptionsType;
  askStateResidencyDetails?: boolean;
  studentOptions: DriverStudentInfoProps['options'];
  askForOccupation?: boolean;
  askForEducation?: boolean;
};

export const DriverAttributes: FC<DriverAttributesProps> = ({
  options,
  askStateResidencyDetails,
  studentOptions,
  askForOccupation = true,
  askForEducation = true,
}) => {
  const { handleBlur, setFieldValue, values } = useFormikContext<
    CoveredPartyPayloadType
  >();
  const {
    details: { mailingState },
  } = useContext(PolicyContext);

  return (
    <div className={styles.section} data-testid="driver-modal-attributes">
      <h1>Driver Attributes</h1>
      <Row>
        {(askForEducation ?? true) && (
          <Column className={styles.driverInfoCol}>
            <FormInput
              variant="selectAutocomplete"
              name="attributes.educationLevel"
              data-testid="education-level"
              onChange={value =>
                setFieldValue('attributes.educationLevel', value)
              }
              onBlur={handleBlur}
              value={values.attributes.educationLevel}
              label="Education Level"
              options={options.educationLevel ?? {}}
            />
          </Column>
        )}
        {(askForOccupation ?? true) && (
          <Column className={styles.driverInfoCol}>
            <FormInput
              variant="selectAutocomplete"
              name="attributes.occupation"
              data-testid="occupation"
              onChange={value => setFieldValue('attributes.occupation', value)}
              onBlur={handleBlur}
              value={values.attributes.occupation}
              label="Occupation"
              options={options.occupations}
            />
          </Column>
        )}
        <Column className={styles.driverInfoCol}>
          <FormInput
            variant="radioButtonGroup"
            name="attributes.homeowner"
            data-testid="homeowner"
            onChange={value => setFieldValue('attributes.homeowner', value)}
            onBlur={handleBlur}
            value={values.attributes.homeowner}
            label="Homeowner"
            options={{ true: 'Yes', false: 'No' }}
          />
        </Column>
      </Row>
      {env.driverNewQuestionsEnabled && askStateResidencyDetails ? (
        <>
          <Row>
            <Column className={styles.driverInfoCol}>
              <FormInput
                variant="radioButtonGroup"
                data-testid="in-state-residency-duration"
                label={`In a typical year, does this driver reside in ${mailingState} for at least 10 months?`}
                tooltip="If the driver is an active military member currently deployed (domestic or overseas) or on temporary duty assignment, select ‘Yes’."
                tooltipLabel={
                  <label className={styles.tooltipLabel}>Military</label>
                }
                tooltipClassName={styles.tooltipWrapper}
                name="attributes.inStateResidencyDuration"
                options={{ atLeast10Months: 'Yes', lessThan10Months: 'No' }}
                value={values.attributes.inStateResidencyDuration}
                onChange={value => {
                  if (value === 'atLeast10Months') {
                    setFieldValue(
                      'attributes.isStateResidentWithOutOfStateHomes',
                      ''
                    );
                    setFieldValue('attributes.isOutOfStateStudent', '');
                    setFieldValue(
                      'attributes.hasVehicleWhileOutOfStateStudent',
                      ''
                    );
                  }
                  setFieldValue('attributes.inStateResidencyDuration', value);
                }}
              />
            </Column>
            <Column className={styles.driverInfoCol}>
              <FormInput
                variant="radioButtonGroup"
                data-testid="multiple-households-multiple-states"
                label={`Permanent resident of ${mailingState} with multiple households in multiple states`}
                name="attributes.isStateResidentWithOutOfStateHomes"
                options={{ true: 'Yes', false: 'No' }}
                value={values.attributes.isStateResidentWithOutOfStateHomes}
                onChange={value =>
                  setFieldValue(
                    'attributes.isStateResidentWithOutOfStateHomes',
                    value
                  )
                }
                disabled={
                  values.attributes.inStateResidencyDuration !==
                  'lessThan10Months'
                }
              />
            </Column>
            <Column className={styles.driverInfoCol}>
              <FormInput
                variant="radioButtonGroup"
                data-testid="out-of-state-student"
                label="Full-time student away at school"
                name="attributes.isOutOfStateStudent"
                options={{ true: 'Yes', false: 'No' }}
                value={values.attributes.isOutOfStateStudent}
                onChange={value =>
                  setFieldValue('attributes.isOutOfStateStudent', value)
                }
                disabled={
                  values.attributes.inStateResidencyDuration !==
                  'lessThan10Months'
                }
              />
            </Column>
          </Row>
          <Row>
            <Column className={styles.driverInfoCol}>
              <FormInput
                variant="radioButtonGroup"
                data-testid="own-vehicle-while-out-of-state-student"
                label={`Has possession of an insured vehicle under this policy while attending school outside of ${mailingState}`}
                name="attributes.hasVehicleWhileOutOfStateStudent"
                options={{ true: 'Yes', false: 'No' }}
                value={values.attributes.hasVehicleWhileOutOfStateStudent}
                onChange={value => {
                  setFieldValue(
                    'attributes.hasVehicleWhileOutOfStateStudent',
                    value
                  );
                }}
                disabled={
                  values.attributes.inStateResidencyDuration !==
                  'lessThan10Months'
                }
              />
            </Column>
            <DriverStudentInfo options={studentOptions} />
            <Column></Column>
          </Row>
        </>
      ) : (
        <Row>
          <DriverStudentInfo options={studentOptions} />
          <Column />
          <Column />
        </Row>
      )}
    </div>
  );
};
